<template>
  <div class="writing" role="status">
    <AiLoading inline :label="label" />

    <div class="lines" aria-hidden="true">
      <span v-for="(width, index) in lineWidths" :key="index" class="line" :style="{ '--line': index }">
        <Skeleton v-if="width" :width="width" height="0.5rem" :style="{ animationDelay: `${index * 80}ms` }" />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTimeout } from '@vueuse/core';
import { computed } from 'vue';

import { AiLoading, Skeleton } from '@tallo/design-system';

import { usePropertyStore } from '../../../stores/property.store';

// The shape of the listing on its way: an opening paragraph, "The details:" over its list, then the tours line.
// An empty entry is a blank line.
const lineWidths = ['100%', '96%', '99%', '93%', '61%', '', '24%', '54%', '43%', '62%', '38%', '49%', '', '36%'];

const propertyStore = usePropertyStore();
// Most descriptions land in about 4 seconds, so a wait past 7 gets told it is still going
const isTakingLonger = useTimeout(7000);

const label = computed(() => {
  if (isTakingLonger.value) {
    return 'Still drafting, a few more seconds…';
  }

  return propertyStore.images.length
    ? "Drafting from your property's details and photos…"
    : "Drafting from your property's details…";
});
</script>

<style scoped lang="scss">
.writing {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-sm);
  overflow: hidden;
  // Same padding as the textarea, so each line sits where a line of text will
  padding: 0.75rem var(--t-spacing-md);
  border-radius: var(--t-border-radius-sm);
  background-color: var(--t-color-neutral-white);
}

.line {
  display: flex;
  align-items: center;
  height: var(--t-typography-body-md-line-height);
  animation: line-in 240ms var(--t-ease-out) both;
  animation-delay: calc(var(--line) * 30ms);
}

@keyframes line-in {
  from {
    opacity: 0;
    transform: translateY(0.25rem);
  }
}

@keyframes line-fade-in {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .line {
    animation-name: line-fade-in;
    animation-delay: 0ms;
  }
}
</style>
